import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Edit2,
  Loader2,
  Plus,
  Printer,
  Share2,
  Upload,
  User,
  Wand2,
  X,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { supabase } from '../../../lib/supabase';
import { gameService, type Game, type GameLineupEntry } from '../../../services/gameService';
import { playerCardService } from '../../../services/playerCardService';
import { sportStatsService } from '../../../services/sportStatsService';
import { parseGameChangerCsvToPlayerStats } from '../../../services/gamechangerImport';
import { getCalculatedStats } from '../../../utils/statCalculations';
import { getGameFieldingInningsCount } from '../../../utils/gameFieldingInnings';
import { isAndroid, isMobile } from '../../../utils/mobile';
import { SPORT_RULESETS } from '../../../data/sportRulesets';
import { SPORT_POSITIONS } from '../../../data/sportsPositions';
import { useDepthChartRandomizer } from '../../../hooks/useDepthChartRandomizer';
import RandomizationModal from '../../RandomizationModal';
import { InjuryBadge } from '../../ui/InjuryBadge';
import { BaseballDiamondFieldingView } from '../../depth-chart/BaseballDiamondFieldingView';
import { BaseballLineupGrid, type LineupSlotChangeMeta, useNarrowViewport } from '../../depth-chart/BaseballLineupGrid';
import { INJURY_STATUS } from '../../../constants/injuries';
import type { InjuryStatus } from '../../../constants/injuries';
import type { DepthChartEntry } from '../../../types/depth-chart';
import type { Player, StatWithId, Team } from '../../../types';
import { GameDetailsPlayerStatsSummary } from '../GameDetailsPlayerStatsSummary';
import { applyGameLineupDraftChange } from './baseballLineupDraft';

interface BaseballGameDetailProps {
  game: Game;
  gameId: string;
  currentTeam: Team;
  allTeamPlayers: Player[];
  setAllTeamPlayers: React.Dispatch<React.SetStateAction<Player[]>>;
  playerCards: Map<string, string>;
  playerStats: Map<string, StatWithId[]>;
  calculatedStats: Map<string, Record<string, number>>;
  /** Batting order, loaded/owned by the parent since it also feeds the shared record-stats ordering used by every sport. */
  lineup: Array<{ order: number; player: Player | null }>;
  expandedStatsPlayerIds: Set<string>;
  hasExistingGameStats: boolean;
  onToggleStatsExpanded: (playerId: string) => void;
  onEditStats: (playerId: string, cardId: string) => void;
  /** Re-fetch the game row after lineup_source changes. */
  refreshGame: () => Promise<void>;
  /** Re-fetch per-player stats after a CSV import. */
  loadGameStats: () => Promise<void>;
  /** Re-fetch the batting order after saving it (parent owns `lineup`). */
  loadLineup: (game: Game) => Promise<void>;
}

/** Baseball/Softball-specific game detail: box score, lineup management, fielding, and CSV stats import. */
export function BaseballGameDetail({
  game,
  gameId,
  currentTeam,
  allTeamPlayers,
  setAllTeamPlayers,
  playerCards,
  playerStats,
  calculatedStats,
  lineup,
  expandedStatsPlayerIds,
  hasExistingGameStats,
  onToggleStatsExpanded,
  onEditStats,
  refreshGame,
  loadGameStats,
  loadLineup,
}: BaseballGameDetailProps) {
  const lineupModalNarrow = useNarrowViewport(768);

  const [showSetLineupModal, setShowSetLineupModal] = useState(false);
  const [manualLineupDraft, setManualLineupDraft] = useState<(string | null)[]>(Array(9).fill(null));
  /** Season cumulative AVG/OBP (and bats) for lineup modal — same source as Depth Chart batting grid. */
  const [lineupEditorPlayerStatsMap, setLineupEditorPlayerStatsMap] = useState<
    Map<string, { batting_average?: number; on_base_percentage?: number; batting_hand?: 'L' | 'R' | 'S' }>
  >(new Map());
  const [pendingOutPlayerSelection, setPendingOutPlayerSelection] = useState<{
    order: number;
    playerId: string;
    pendingProceed?: () => void;
  } | null>(null);
  const [showImportCsvModal, setShowImportCsvModal] = useState(false);
  const [csvImporting, setCsvImporting] = useState(false);
  const [csvImportStatus, setCsvImportStatus] = useState<string | null>(null);
  const csvFileInputRef = useRef<HTMLInputElement>(null);
  const [showReplaceStatsConfirm, setShowReplaceStatsConfirm] = useState(false);
  const pendingCsvFileRef = useRef<File | null>(null);
  const [sharingLineup, setSharingLineup] = useState(false);

  const [fieldingInning, setFieldingInning] = useState<number>(1);
  const [fieldingTotalInnings, setFieldingTotalInnings] = useState<number>(() => SPORT_RULESETS.Baseball.defaultInnings ?? 7);
  const [fieldingEntries, setFieldingEntries] = useState<Array<{ position: string; depth: number; player_id: string | null }>>([]);
  const [savingFielding, setSavingFielding] = useState(false);
  const saveFieldingTimerRef = useRef<number | null>(null);
  const [gameFieldingSupported, setGameFieldingSupported] = useState(true);
  const gameFieldingMissingToastRef = useRef(false);
  /** Android/iOS WebView `<select>` is unreliable (broken dialog, wrong options); use a custom sheet instead. */
  const [fieldingInningSheetOpen, setFieldingInningSheetOpen] = useState(false);

  const pitchesByPlayerId = useMemo(() => {
    const map = new Map<string, number>();
    playerStats.forEach((stats, playerId) => {
      const total = stats
        .filter((s) => s.stat_key === 'Pitches')
        .reduce((acc, s) => acc + (Number.isFinite(s.stat_value) ? s.stat_value : 0), 0);
      if (total > 0) map.set(playerId, total);
    });
    return map;
  }, [playerStats]);

  const regulationFieldingInnings = useMemo(() => {
    return getGameFieldingInningsCount(currentTeam);
  }, [currentTeam.id, currentTeam.sport, currentTeam.innings]);

  /** Never below team/regulation innings; guards bad or transient state (e.g. NaN). */
  const displayFieldingTotalInnings = useMemo(() => {
    const stored = Number(fieldingTotalInnings);
    const safeStored = Number.isFinite(stored) && stored >= 1 ? stored : regulationFieldingInnings;
    return Math.max(1, regulationFieldingInnings, safeStored);
  }, [fieldingTotalInnings, regulationFieldingInnings]);

  const fieldingRandomizerPositions = useMemo(() => {
    const sport = currentTeam.sport ?? 'Baseball';
    const defensePositions = (SPORT_POSITIONS as any)[sport]?.Defense ?? [];
    return defensePositions.map((p: any) => ({
      key: p.key,
      name: p.key,
      fullName: p.name ?? p.key,
      color: ''
    }));
  }, [currentTeam.sport]);

  const fieldingRandomizerCurrentDepthChart = useMemo(() => {
    return fieldingEntries
      .filter((e) => e.player_id)
      .map(
        (e): DepthChartEntry =>
          ({
            team_id: currentTeam.id,
            unit: 'Fielding',
            position: e.position,
            depth: e.depth,
            player_id: e.player_id!,
            color: ''
          }) as any
      );
  }, [currentTeam.id, fieldingEntries]);

  const fieldingRandomizer = useDepthChartRandomizer({
    players: allTeamPlayers,
    positions: fieldingRandomizerPositions as any,
    currentDepthChart: fieldingRandomizerCurrentDepthChart as any,
    activeTab: 'Fielding',
    sport: (currentTeam.sport as any) ?? 'Baseball',
    teamId: currentTeam.id,
    team: currentTeam as any,
    fieldingInnings: displayFieldingTotalInnings,
    batteryStorageId: `game-${gameId}`,
    onRandomizationComplete: (entries: any[]) => {
      setFieldingEntries(
        entries.map((e: any) => ({
          position: e.position,
          depth: e.depth,
          player_id: e.player_id ?? null
        }))
      );
    }
  });

  // Regulation inning count from team settings (or ruleset default). Preserve user-added extra innings via `prev`.
  useEffect(() => {
    const base = getGameFieldingInningsCount(currentTeam);
    setFieldingTotalInnings((prev) => Math.max(base, prev));
    setFieldingInning(1);
  }, [currentTeam.id, currentTeam.innings, currentTeam.sport]);

  useEffect(() => {
    setFieldingInning(1);
  }, [gameId]);

  useEffect(() => {
    setFieldingInning((i) => Math.min(Math.max(1, i), displayFieldingTotalInnings));
  }, [displayFieldingTotalInnings]);

  useEffect(() => {
    setFieldingInningSheetOpen(false);
  }, [gameId]);

  const loadFielding = useCallback(
    async (gameData: Game) => {
      try {
        // Note: allTeamPlayers is populated (with player_cards enrichment) by the
        // parent's loadAllTeamPlayers(). Do NOT set it here — this path loads raw
        // players rows without eligible_positions/role_types and would overwrite
        // the enriched data, causing the field builder to ignore position eligibility.

        // Prefer per-game fielding; fall back to team depth chart (unit = 'Fielding')
        let entries = await gameService.getGameFielding(gameData.id);
        setGameFieldingSupported(true);
        if (entries.length === 0) {
          const { data: depthChartData, error } = await supabase
            .from('depth_chart')
            .select('player_id, position, depth')
            .eq('team_id', currentTeam.id)
            .eq('unit', 'Fielding');
          if (!error && depthChartData) {
            entries = depthChartData.map((e: any) => ({
              inning: e.depth,
              position: e.position,
              player_id: e.player_id ?? null
            }));
          }
        }

        const mapped = entries.map((e) => ({
          position: e.position,
          depth: e.inning,
          player_id: e.player_id
        }));
        const maxInningFromData = mapped.reduce((m, row) => Math.max(m, Number(row.depth) || 0), 0);
        const baseTotal = getGameFieldingInningsCount(currentTeam);
        setFieldingTotalInnings(Math.max(1, baseTotal, maxInningFromData));
        setFieldingEntries(mapped);
      } catch (e) {
        console.error('Error loading fielding:', e);
      }
    },
    [currentTeam.id, currentTeam.innings, currentTeam.sport]
  );

  useEffect(() => {
    void loadFielding(game);
  }, [game, loadFielding]);

  // Auto-save fielding changes (debounced)
  useEffect(() => {
    if (!gameFieldingSupported) return;
    if (saveFieldingTimerRef.current) {
      window.clearTimeout(saveFieldingTimerRef.current);
    }
    saveFieldingTimerRef.current = window.setTimeout(async () => {
      setSavingFielding(true);
      try {
        await gameService.setGameFielding(
          gameId,
          fieldingEntries.map((e) => ({
            inning: e.depth,
            position: e.position,
            player_id: e.player_id
          }))
        );
      } catch (e) {
        console.error('Failed to auto-save fielding:', e);
        const msg = e instanceof Error ? e.message : '';
        if (msg.includes("Missing table 'public.game_fielding'")) {
          setGameFieldingSupported(false);
          if (!gameFieldingMissingToastRef.current) {
            gameFieldingMissingToastRef.current = true;
            toast.error("Local DB is missing 'game_fielding'. Run `supabase db reset` and reload.");
          }
          return;
        }
        toast.error('Failed to save fielding');
      } finally {
        setSavingFielding(false);
      }
    }, 600);

    return () => {
      if (saveFieldingTimerRef.current) {
        window.clearTimeout(saveFieldingTimerRef.current);
        saveFieldingTimerRef.current = null;
      }
    };
  }, [fieldingEntries, gameId, gameFieldingSupported]);

  useEffect(() => {
    let cancelled = false;
    const loadSeasonStatsForLineupEditor = async () => {
      try {
        const cards = await playerCardService.getPlayerCardsForTeam(currentTeam.id);
        const statsMap = new Map<
          string,
          { batting_average?: number; on_base_percentage?: number; batting_hand?: 'L' | 'R' | 'S' }
        >();
        for (const card of cards) {
          if (!card.player?.id) continue;
          const battingHand = card.batting_hand || card.player.batting_hand;
          const allStats = await sportStatsService.getStats(card.id, currentTeam.id);
          if (cancelled) return;
          if (allStats.length > 0) {
            const rawStats = allStats.map((s: { stat_key: string; stat_value: number }) => ({
              stat_key: s.stat_key,
              stat_value: s.stat_value,
            }));
            const calculated = getCalculatedStats(currentTeam.sport, rawStats, currentTeam.innings);
            statsMap.set(card.player.id, {
              batting_average: calculated.AVG,
              on_base_percentage: calculated.OBP,
              batting_hand: battingHand as 'L' | 'R' | 'S' | undefined,
            });
          } else if (battingHand) {
            statsMap.set(card.player.id, {
              batting_hand: battingHand as 'L' | 'R' | 'S' | undefined,
            });
          }
        }
        if (!cancelled) setLineupEditorPlayerStatsMap(statsMap);
      } catch (error) {
        console.error('Error loading season stats for lineup editor:', error);
      }
    };
    void loadSeasonStatsForLineupEditor();
    return () => {
      cancelled = true;
    };
  }, [currentTeam.id, currentTeam.sport, allTeamPlayers.length]);

  const matchPlayer = (number: string, last: string, first: string): Player | null => {
    const numTrim = number.trim();
    const lastNorm = last.trim().toLowerCase();
    const firstNorm = first.trim().toLowerCase();
    const normalizeName = (s: string) =>
      s
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim()
        .replace(/\s+/g, ' ');
    const byNumber = numTrim !== '' && allTeamPlayers.find((p) => p.number != null && String(p.number).trim() === numTrim);
    if (byNumber) return byNumber;
    const targetFull = normalizeName(`${firstNorm} ${lastNorm}`);
    return (
      allTeamPlayers.find((p) => normalizeName(`${p.first_name ?? ''} ${p.last_name ?? ''}`) === targetFull) ??
      allTeamPlayers.find(
        (p) =>
          normalizeName(p.last_name ?? '') === normalizeName(lastNorm) &&
          normalizeName(p.first_name ?? '') === normalizeName(firstNorm)
      ) ??
      null
    );
  };

  const runCsvImport = async (file: File) => {
    setCsvImporting(true);
    setCsvImportStatus('Reading file…');
    try {
      const readTextWithTimeout = async (ms: number): Promise<string> => {
        const timeout = new Promise<string>((_, reject) =>
          setTimeout(() => reject(new Error('Timed out reading file. If this is a Google Drive file, try downloading it locally first.')), ms)
        );
        const read = (async () => {
          // Prefer modern File APIs — these are more reliable with Drive-backed content.
          if (typeof (file as any).text === 'function') {
            return await file.text();
          }
          const buf = await file.arrayBuffer();
          return new TextDecoder('utf-8', { fatal: false }).decode(buf);
        })();
        return await Promise.race([read, timeout]);
      };

      const text = await readTextWithTimeout(15_000);
      setCsvImportStatus('Parsing CSV…');
      const parsed = parseGameChangerCsvToPlayerStats(text);
      if (parsed.length === 0) {
        toast.error('No player rows found in the CSV');
        setCsvImportStatus(null);
        setCsvImporting(false);
        return;
      }
      const noBattingButHasPitching =
        parsed.every((r) => r.batting.length === 0) && parsed.some((r) => r.pitching.length > 0);
      let imported = 0;
      const skipped: string[] = [];
      const date = game.date;
      const opponent = game.opponent_name ?? undefined;
      const season = game.season ?? undefined;
      for (const row of parsed) {
        const player = matchPlayer(row.number, row.last, row.first);
        if (!player) {
          skipped.push(`${row.first} ${row.last} (#${row.number})`);
          continue;
        }
        const playerCardId = playerCards.get(player.id);
        if (!playerCardId) {
          skipped.push(`${player.first_name} ${player.last_name} (no player card)`);
          continue;
        }
        setCsvImportStatus(`Importing ${player.first_name} ${player.last_name}…`);
        for (const { stat_key, value } of [...row.batting, ...row.pitching]) {
          await sportStatsService.addStat(playerCardId, currentTeam.id, stat_key, value, date, gameId, opponent, season);
        }
        imported += 1;
      }
      loadGameStats();
      setShowImportCsvModal(false);
      setShowReplaceStatsConfirm(false);
      setCsvImportStatus(null);
      setCsvImporting(false);
      if (imported === 0) {
        toast.error(
          'Could not import any data. The file format may be incorrect or no players matched. Check that the CSV has "Number", "Last", and "First" columns and that player names/numbers match your roster.'
        );
        return;
      }
      if (noBattingButHasPitching) {
        toast(
          'No batting data was found in this file; only pitching stats were imported. Check that the file has a "Batting" section in the header row.',
          { icon: '⚠️', duration: 6000 }
        );
      }
      if (skipped.length > 0) {
        toast.success(`Imported stats for ${imported} player(s). Skipped: ${skipped.slice(0, 3).join(', ')}${skipped.length > 3 ? ` and ${skipped.length - 3} more` : ''}`);
      } else {
        toast.success(`Imported stats for ${imported} player(s).`);
      }
    } catch (err: any) {
      console.error('CSV import error:', err);
      toast.error(err?.message ?? 'Failed to import CSV');
      setCsvImportStatus(null);
      setCsvImporting(false);
    }
  };

  const handleCsvFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    if (hasExistingGameStats) {
      pendingCsvFileRef.current = file;
      setShowReplaceStatsConfirm(true);
      return;
    }

    await runCsvImport(file);
  };

  const handleConfirmReplaceCsvImport = async () => {
    const file = pendingCsvFileRef.current;
    if (!file) {
      setShowReplaceStatsConfirm(false);
      pendingCsvFileRef.current = null;
      return;
    }
    setCsvImporting(true);
    setCsvImportStatus('Clearing existing stats…');
    try {
      await sportStatsService.deleteStatsByGameId(gameId);
      pendingCsvFileRef.current = null;
      await runCsvImport(file);
    } catch (err: any) {
      console.error('Replace/import error:', err);
      toast.error(err?.message ?? 'Failed to replace stats');
      setCsvImportStatus(null);
      setCsvImporting(false);
      setShowReplaceStatsConfirm(false);
      pendingCsvFileRef.current = null;
    }
  };

  const handleCancelReplaceCsvImport = () => {
    setShowReplaceStatsConfirm(false);
    pendingCsvFileRef.current = null;
  };

  const openSetLineupModal = () => {
    const maxOrder = lineup.length ? Math.max(...lineup.map((e) => e.order), 9) : 9;
    const draft: (string | null)[] = Array(maxOrder).fill(null);
    lineup.forEach(({ order, player }) => {
      const idx = order - 1;
      if (idx >= 0 && player) draft[idx] = player.id;
    });
    setManualLineupDraft(draft);
    setShowSetLineupModal(true);
  };

  const fillDraftFromDepthChart = useCallback(async () => {
    try {
      const { data: depthChartData, error } = await supabase
        .from('depth_chart')
        .select('*')
        .eq('team_id', currentTeam.id)
        .eq('unit', 'Lineup')
        .eq('depth', 1)
        .order('position', { ascending: true });

      if (error) {
        console.error('Error loading depth chart:', error);
        toast.error('Could not load depth chart');
        return;
      }

      const draft: (string | null)[] = Array(Math.max(9, (depthChartData || []).length)).fill(null);
      (depthChartData || []).forEach((entry: DepthChartEntry) => {
        const idx = (parseInt(entry.position, 10) || 1) - 1;
        if (idx >= 0) draft[idx] = entry.player_id;
      });
      setManualLineupDraft(draft);
      toast.success('Filled from Depth Chart. Edit if needed, then Save.');
    } catch (e) {
      toast.error('Could not load depth chart');
    }
  }, [currentTeam.id]);

  const saveManualLineup = async () => {
    const entries: GameLineupEntry[] = manualLineupDraft
      .map((playerId, i) => (playerId ? { batting_order: i + 1, player_id: playerId } : null))
      .filter((e): e is GameLineupEntry => e != null);
    try {
      await gameService.setGameLineup(gameId, entries);
      if (game.lineup_source !== 'manual') {
        await gameService.updateGame(gameId, { lineup_source: 'manual' });
        await refreshGame();
      }
      toast.success('Lineup saved');
      setShowSetLineupModal(false);
      await loadLineup({ ...game, lineup_source: 'manual' });
    } catch (e) {
      toast.error('Failed to save lineup');
    }
  };

  const updateFieldingPlayer = useCallback((position: string, inning: number, playerId: string | null) => {
    setFieldingEntries((prev) => {
      const next = prev.filter((e) => !(e.position === position && e.depth === inning));
      if (!playerId) return next;
      next.push({ position, depth: inning, player_id: playerId });
      return next;
    });
  }, []);

  const showFieldingRandomizeModal = useCallback(() => {
    fieldingRandomizer.showRandomizationModal();
  }, [fieldingRandomizer]);

  const playersWithCards = allTeamPlayers.filter((p) => playerCards.has(p.id));
  const lineupIsEmpty = lineup.length === 0;
  const lineupDraftForGrid = useMemo(() => {
    const slotCount = Math.max(manualLineupDraft.length, 9);
    return Array.from({ length: slotCount }, (_, i) => {
      const order = i + 1;
      const pid = manualLineupDraft[i] ?? null;
      const player = pid ? allTeamPlayers.find((p) => p.id === pid) ?? null : null;
      return { order, player };
    });
  }, [manualLineupDraft, allTeamPlayers]);

  const handleGameLineupDraftChange = useCallback(
    (order: number, playerId: string | null, meta?: LineupSlotChangeMeta) => {
      setManualLineupDraft((prev) => applyGameLineupDraftChange(prev, order, playerId, meta));
    },
    []
  );

  const confirmSetOutPlayerHealthyAndSelect = useCallback(async () => {
    if (!pendingOutPlayerSelection) return;
    const { playerId, pendingProceed } = pendingOutPlayerSelection;
    const player = allTeamPlayers.find((p) => p.id === playerId);
    if (!player) {
      setPendingOutPlayerSelection(null);
      return;
    }

    try {
      const { error } = await supabase
        .from('players')
        .update({ injury_status: INJURY_STATUS.HEALTHY })
        .eq('id', playerId)
        .eq('team_id', currentTeam.id);

      if (error) throw error;

      setAllTeamPlayers((prev) =>
        prev.map((p) => (p.id === playerId ? { ...p, injury_status: INJURY_STATUS.HEALTHY } : p))
      );

      pendingProceed?.();
    } catch (e: any) {
      console.error('Failed to update player availability:', e);
      toast.error('Failed to update player availability');
    } finally {
      setPendingOutPlayerSelection(null);
    }
  }, [allTeamPlayers, currentTeam.id, pendingOutPlayerSelection, setAllTeamPlayers]);

  // Game details: all batting order entries (not just 9) for display
  const gameDetailsLineupEntries = useMemo(
    () =>
      lineup
        .filter((e): e is { order: number; player: Player } => e.player != null)
        .sort((a, b) => a.order - b.order),
    [lineup]
  );

  const printGameFielding = useCallback(() => {
    const lineupPlayers = gameDetailsLineupEntries.map((e) => e.player);
    const totalInnings = displayFieldingTotalInnings;
    const getPositionsFor = (playerId: string, inning: number) =>
      fieldingEntries
        .filter((fe) => fe.depth === inning && fe.player_id === playerId)
        .map((fe) => fe.position)
        .join(', ');

    const rowsHtml = lineupPlayers
      .map((p, idx) => {
        const cells = Array.from({ length: totalInnings }, (_, i) => {
          const inning = i + 1;
          const pos = getPositionsFor(p.id, inning);
          if (!pos) return `<td class="fielding-position-cell fielding-sit-out" title="Sitting out"></td>`;
          return `<td class="fielding-position-cell"><div class="fielding-position">${pos}</div></td>`;
        }).join('');
        const name = `${p.first_name ?? ''} ${p.last_name ?? ''}`.trim();
        const num = p.number != null ? `#${p.number}` : '';
        return `<tr>
          <td class="lineup-player-cell"><div class="player-info">${idx + 1}. ${num} ${name}</div></td>
          ${cells}
        </tr>`;
      })
      .join('');

    const inningHeaders = Array.from({ length: totalInnings }, (_, i) => `<th>${i + 1}</th>`).join('');

    const html = `<!doctype html>
      <html>
        <head>
          <title>${currentTeam.name} - Fielding</title>
          <style>
            @page baseballLandscape { size: landscape; margin: 0.5in; }
            body { font-family: Arial, sans-serif; margin: 0; }
            .print-container { page: baseballLandscape; padding: 12px; }
            h1 { margin: 0 0 10px 0; font-size: 18pt; }
            table { width: 100%; border-collapse: collapse; table-layout: fixed; }
            th, td { border: 2px solid #333; padding: 10px 6px; text-align: center; vertical-align: middle; }
            th { background: #2c3e50; color: #fff; font-size: 12pt; }
            .lineup-player-cell { text-align: left; width: 22%; }
            .player-info { font-weight: bold; white-space: nowrap; }
            .fielding-position { font-weight: bold; }
            .fielding-sit-out { background: #fca5a5 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          </style>
        </head>
        <body>
          <div class="print-container">
            <h1>${currentTeam.name} - Fielding (${game.opponent_name})</h1>
            <table>
              <thead>
                <tr>
                  <th class="lineup-player-cell">Batting Order</th>
                  ${inningHeaders}
                </tr>
              </thead>
              <tbody>
                ${rowsHtml}
              </tbody>
            </table>
          </div>
        </body>
      </html>`;

    // Android native printing: use native print dialog (includes Save as PDF)
    if (isMobile() && isAndroid()) {
      (async () => {
        try {
          const { Printer } = await import('@bcyesil/capacitor-plugin-printer');
          await (Printer as any).print({ content: html, name: `${currentTeam.name} - Fielding` });
        } catch (e) {
          console.error('Native print failed, falling back to browser print:', e);
          const printWindow = window.open('', '_blank');
          if (!printWindow) return;
          printWindow.document.open();
          printWindow.document.write(html);
          printWindow.document.close();
          setTimeout(() => printWindow.print(), 250);
        }
      })();
      return;
    }

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 250);
  }, [currentTeam, displayFieldingTotalInnings, fieldingEntries, game, gameDetailsLineupEntries]);

  const handleShareLineup = async () => {
    setSharingLineup(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        toast.error('Sign in to create a share link');
        return;
      }
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
      const res = await fetch(
        `${supabaseUrl}/functions/v1/share-lineup?action=create&gameId=${encodeURIComponent(gameId)}`,
        {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
          },
        },
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.error || 'Failed to create share link');
      const token = json?.token as string | undefined;
      if (!token) throw new Error('Failed to create share link');
      const appOrigin = import.meta.env.VITE_APP_URL || 'https://hustli.app';
      const url = `${appOrigin}/share/lineup/${token}`;
      await navigator.clipboard.writeText(url);
      toast.success('Lineup link copied to clipboard');
    } catch (e: any) {
      console.error('[GameDetailView] share lineup error:', e);
      toast.error(e?.message || 'Failed to create share link');
    } finally {
      setSharingLineup(false);
    }
  };

  return (
    <>
      <div className="flex w-full gap-2 mb-4">
        <button
          type="button"
          onClick={() => setShowImportCsvModal(true)}
          className="flex-1 inline-flex items-center justify-center gap-1.5 py-2.5 px-3 text-sm font-semibold bg-[#e3f4f6] text-[#3da8b4] border border-[#a1d9e0] rounded-xl hover:bg-[#d5eef1] transition"
        >
          <Upload size={16} className="shrink-0" />
          <span className="hidden sm:inline">Import</span>
        </button>
        <button
          type="button"
          onClick={openSetLineupModal}
          className="flex-1 inline-flex items-center justify-center gap-1.5 py-2.5 px-3 text-sm font-semibold bg-[#e3f4f6] text-[#3da8b4] border border-[#a1d9e0] rounded-xl hover:bg-[#d5eef1] transition"
        >
          <Edit2 size={16} className="shrink-0" />
          <span className="hidden sm:inline">Lineup</span>
        </button>
        {lineup.length > 0 && (
          <button
            type="button"
            onClick={handleShareLineup}
            disabled={sharingLineup}
            className="flex-1 inline-flex items-center justify-center py-2.5 px-3 font-semibold bg-[#e3f4f6] text-[#3da8b4] border border-[#a1d9e0] rounded-xl hover:bg-[#d5eef1] transition disabled:opacity-60"
            aria-label="Share game sheet"
            title="Copy share link for full game sheet (batting + fielding)"
          >
            {sharingLineup ? <Loader2 size={16} className="animate-spin" aria-hidden /> : <Share2 size={16} aria-hidden />}
          </button>
        )}
        <button
          type="button"
          onClick={printGameFielding}
          className="flex-1 inline-flex items-center justify-center py-2.5 px-3 font-semibold bg-[#e3f4f6] text-[#3da8b4] border border-[#a1d9e0] rounded-xl hover:bg-[#d5eef1] transition"
          aria-label="Print fielding grid"
          title="Print fielding"
        >
          <Printer size={16} aria-hidden />
        </button>
      </div>

      {playerStats.size > 0 ? (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800 flex items-center gap-2">
          <svg className="w-4 h-4 shrink-0" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
          <p className="font-medium">Game Changer data imported successfully.</p>
        </div>
      ) : (
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
          <p className="font-medium">Upload your GameChanger data here.</p>
          <p className="mt-1 text-blue-700">Use the <strong>Import</strong> button to upload a GameChanger stats export. Batting and pitching data that we support will be imported for this game. Note: fielding and some other advanced stats are currently not yet supported.</p>
        </div>
      )}

      {lineupIsEmpty && (
        <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
          <p className="font-medium">No lineup set for this game.</p>
          <p className="mt-1">Click <strong>Set lineup</strong> to choose your batting order. You can copy from your Depth Chart in the modal.</p>
        </div>
      )}

      <div className="mb-6 border border-gray-200 rounded-lg p-4">
        <div className="space-y-3 mb-3">
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-gray-900">Fielding</h3>
            <p className="text-sm text-gray-500">
              If nothing is saved yet, we start from your team’s Depth Chart fielding.
            </p>
          </div>
          <div className="flex flex-nowrap items-center gap-2 min-w-0">
            <div className="flex flex-nowrap items-center gap-2 text-sm text-gray-600 shrink-0">
              <span className="shrink-0">Inning</span>
              {isMobile() ? (
                <>
                  <button
                    type="button"
                    onClick={() => setFieldingInningSheetOpen(true)}
                    className="h-10 min-h-10 px-3 border border-gray-300 rounded-md bg-white inline-flex items-center gap-2 min-w-[6.5rem] justify-between tabular-nums shrink-0 text-sm"
                    aria-label="Select inning"
                    aria-haspopup="dialog"
                    aria-expanded={fieldingInningSheetOpen}
                  >
                    <span className="font-medium text-gray-900">
                      {fieldingInning}
                      <span className="font-normal text-gray-500"> / {displayFieldingTotalInnings}</span>
                    </span>
                    <ChevronDown size={16} className="text-gray-500 shrink-0" aria-hidden />
                  </button>
                  {fieldingInningSheetOpen ? (
                    <div className="fixed inset-0 z-[60] flex flex-col justify-end pointer-events-auto">
                      <button
                        type="button"
                        className="absolute inset-0 w-full h-full bg-black/50 border-0 p-0 cursor-default"
                        aria-label="Close inning picker"
                        onClick={() => setFieldingInningSheetOpen(false)}
                      />
                      <div
                        className="relative mt-auto bg-white rounded-t-2xl shadow-2xl border border-gray-200 border-b-0 p-4 max-h-[min(50vh,420px)] overflow-y-auto pb-[max(1rem,env(safe-area-inset-bottom))]"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="fielding-inning-sheet-title"
                      >
                        <div className="flex items-center justify-between gap-3 mb-3">
                          <p id="fielding-inning-sheet-title" className="text-base font-semibold text-gray-900">
                            Select inning
                          </p>
                          <button
                            type="button"
                            onClick={() => setFieldingInningSheetOpen(false)}
                            className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"
                            aria-label="Close"
                          >
                            <X size={20} />
                          </button>
                        </div>
                        <div className="grid grid-cols-4 gap-2">
                          {Array.from({ length: displayFieldingTotalInnings }, (_, i) => i + 1).map((n) => (
                            <button
                              key={n}
                              type="button"
                              className={
                                n === fieldingInning
                                  ? 'rounded-lg py-3 text-sm font-semibold bg-primary text-white border border-primary'
                                  : 'rounded-lg py-3 text-sm font-medium bg-gray-50 text-gray-900 border border-gray-200 hover:bg-gray-100'
                              }
                              onClick={() => {
                                setFieldingInning(n);
                                setFieldingInningSheetOpen(false);
                              }}
                            >
                              {n}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </>
              ) : (
                <select
                  value={fieldingInning}
                  onChange={(e) => setFieldingInning(parseInt(e.target.value, 10) || 1)}
                  className="h-10 min-h-10 box-border px-3 border border-gray-300 rounded-md bg-white shrink-0 text-sm"
                  aria-label="Select inning"
                >
                  {Array.from({ length: displayFieldingTotalInnings }, (_, i) => i + 1).map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <button
              type="button"
              onClick={() => setFieldingTotalInnings((v) => v + 1)}
              className="btn-secondary inline-flex h-10 min-h-10 items-center gap-1.5 px-3 py-0 text-xs shrink-0 whitespace-nowrap"
              title={`Add extra inning (currently ${displayFieldingTotalInnings} innings)`}
              aria-label={`Add inning, currently ${displayFieldingTotalInnings} innings`}
            >
              <Plus size={14} aria-hidden />
              Add Inning ({displayFieldingTotalInnings})
            </button>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:flex-wrap">
            <button
              type="button"
              onClick={showFieldingRandomizeModal}
              className="btn-secondary flex items-center justify-center gap-1.5 text-sm w-full sm:w-auto shrink-0"
              aria-label={fieldingRandomizer.randomizeButtonText}
            >
              <Wand2 size={16} />
              {fieldingRandomizer.randomizeButtonText}
            </button>
            {savingFielding ? <span className="text-xs text-gray-500 sm:ml-1">Saving…</span> : null}
          </div>
        </div>

        {allTeamPlayers.length > 0 ? (
          <BaseballDiamondFieldingView
            players={allTeamPlayers}
            depthChart={fieldingEntries}
            sport={currentTeam.sport || 'Baseball'}
            currentInning={fieldingInning}
            onPlayerChange={updateFieldingPlayer}
            playerSortBy="number"
            totalInnings={displayFieldingTotalInnings}
            hideHeaderActions
            pitchesByPlayerId={pitchesByPlayerId}
          />
        ) : (
          <div className="text-sm text-gray-500">No available players to assign.</div>
        )}

        <RandomizationModal
          isOpen={fieldingRandomizer.showRandomizeModal}
          onClose={() => fieldingRandomizer.setShowRandomizeModal(false)}
          onConfirm={fieldingRandomizer.executeRandomization}
          isRandomizing={fieldingRandomizer.isRandomizing}
          settings={fieldingRandomizer.randomizationSettings as any}
          onSettingsChange={fieldingRandomizer.updateRandomizationSettings as any}
          preview={fieldingRandomizer.randomizationPreview as any}
          title={fieldingRandomizer.randomizeButtonText}
          description={fieldingRandomizer.randomizeDescription}
          sport={currentTeam.sport || 'Baseball'}
          activeTab="Fielding"
          players={allTeamPlayers as any}
          positions={fieldingRandomizerPositions as any}
          teamInnings={displayFieldingTotalInnings}
        />
      </div>

      {playersWithCards.length === 0 && (
        <p className="text-sm text-gray-500">No players with player cards yet. Add player cards from Roster to record stats for this game.</p>
      )}

      {playersWithCards.length > 0 && gameDetailsLineupEntries.length > 0 && (
        <div className="space-y-1">
          <h3 className="text-sm font-semibold text-gray-700 mb-2 mt-4">Player Stats</h3>
          {gameDetailsLineupEntries.map(({ order, player }) => {
            const isExpanded = expandedStatsPlayerIds.has(player.id);
            const cardId = playerCards.get(player.id);
            return (
              <div key={player.id} className="rounded border border-gray-200 bg-white overflow-hidden">
                <div
                  className="flex items-center gap-2 p-2 w-full text-left cursor-pointer"
                  role="button"
                  tabIndex={0}
                  aria-label={isExpanded ? 'Collapse player stats' : 'Expand player stats'}
                  onClick={() => onToggleStatsExpanded(player.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onToggleStatsExpanded(player.id);
                    }
                  }}
                >
                  <span className="flex-shrink-0 w-6 text-sm font-semibold text-gray-500 tabular-nums">{order}.</span>
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {player.number != null ? (
                      <span className="font-bold text-primary text-sm min-w-[2rem]">#{player.number}</span>
                    ) : (
                      <User size={16} className="text-gray-400 flex-shrink-0" />
                    )}
                    <span className="font-medium text-sm text-gray-900">
                      {player.first_name ?? ''} {player.last_name ?? ''}
                    </span>
                    {player.injury_status && player.injury_status !== 'H' && (
                      <InjuryBadge injuryStatus={player.injury_status as InjuryStatus} size="xs" showLabel={false} />
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleStatsExpanded(player.id);
                    }}
                    className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-primary transition-colors flex-shrink-0"
                    aria-label={isExpanded ? 'Collapse stats' : 'Expand stats'}
                  >
                    {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                  </button>
                  {cardId && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditStats(player.id, cardId);
                      }}
                      className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-primary transition-colors flex-shrink-0"
                      aria-label="Edit stats"
                    >
                      <Edit2 size={16} />
                    </button>
                  )}
                </div>
                {isExpanded && (
                  <div className="px-2 pb-2 pt-0 border-t border-gray-100 bg-gray-50/80">
                    <GameDetailsPlayerStatsSummary
                      playerId={player.id}
                      rawStats={playerStats.get(player.id) || []}
                      calculated={calculatedStats.get(player.id) || {}}
                      sport={currentTeam.sport}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showSetLineupModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[85vh] flex flex-col">
            <div className="flex-shrink-0 border-b border-gray-200 px-4 py-3 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Set batting order</h3>
              <button type="button" onClick={() => setShowSetLineupModal(false)} className="p-1 hover:bg-gray-100 rounded">
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3">
              <button
                type="button"
                onClick={fillDraftFromDepthChart}
                className="w-full text-sm text-primary border border-primary/50 rounded-lg py-2 hover:bg-primary/5 transition-colors"
              >
                Fill from Depth Chart
              </button>
              <BaseballLineupGrid
                lineup={lineupDraftForGrid}
                allPlayers={allTeamPlayers}
                playerStatsMap={lineupEditorPlayerStatsMap}
                onPlayerChange={handleGameLineupDraftChange}
                interceptAssign={(order, playerId, proceed) => {
                  const selected = allTeamPlayers.find((p) => p.id === playerId);
                  if (selected?.injury_status === INJURY_STATUS.OUT) {
                    setPendingOutPlayerSelection({ order, playerId, pendingProceed: proceed });
                    return;
                  }
                  proceed();
                }}
                slotCount={Math.max(manualLineupDraft.length, 9)}
                headerSubtitle={
                  lineupModalNarrow
                    ? 'Tap a batting slot to choose or swap players (same as Depth Chart lineup). Clear a spot to compact the order.'
                    : 'Drag players between slots to set the batting order (same as Depth Chart lineup).'
                }
              />
              <button
                type="button"
                onClick={() => setManualLineupDraft((prev) => [...prev, null])}
                className="text-sm text-primary hover:underline"
              >
                + Add batting spot
              </button>
            </div>
            <div
              className="flex-shrink-0 border-t border-gray-200 p-4 flex gap-2 bg-white"
              style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)' }}
            >
              <button type="button" onClick={() => setShowSetLineupModal(false)} className="flex-1 btn-secondary py-2">
                Cancel
              </button>
              <button type="button" onClick={saveManualLineup} className="flex-1 btn-primary py-2">
                Save lineup
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingOutPlayerSelection && (() => {
        const player = allTeamPlayers.find((p) => p.id === pendingOutPlayerSelection.playerId);
        const name = player ? `${player.first_name ?? ''} ${player.last_name ?? ''}`.trim() : 'This player';
        return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
              <div className="p-5">
                <div className="text-lg font-semibold text-gray-900">Player marked Out</div>
                <div className="mt-2 text-sm text-gray-700">
                  Player <strong>{name}</strong> is currently marked &quot;Out&quot;. Would you like to move that
                  player&apos;s availability status to &quot;Healthy&quot;?
                </div>
              </div>
              <div className="p-5 pt-0 flex gap-2">
                <button
                  type="button"
                  className="flex-1 btn-secondary py-2"
                  onClick={() => setPendingOutPlayerSelection(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="flex-1 btn-primary py-2"
                  onClick={confirmSetOutPlayerHealthyAndSelect}
                >
                  Yes, proceed
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {showImportCsvModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
              <h3 className="text-lg font-semibold">
                {showReplaceStatsConfirm ? 'Replace existing stats?' : 'Import GameChanger CSV'}
              </h3>
              <button
                type="button"
                onClick={() => {
                  if (!csvImporting) {
                    setShowImportCsvModal(false);
                    setShowReplaceStatsConfirm(false);
                    pendingCsvFileRef.current = null;
                  }
                }}
                className="p-1 hover:bg-gray-100 rounded disabled:opacity-50"
                disabled={csvImporting}
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-4 space-y-4">
              {showReplaceStatsConfirm ? (
                <>
                  <p className="text-sm text-gray-700">
                    This game already has stats recorded. Are you sure you want to replace the existing stats with the GameChanger stats?
                  </p>
                  <div className="rounded-lg border-2 border-red-200 bg-red-50 p-3">
                    <p className="text-sm font-bold text-red-800">This cannot be undone.</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={handleCancelReplaceCsvImport}
                      disabled={csvImporting}
                      className="flex-1 btn-secondary py-2"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleConfirmReplaceCsvImport}
                      disabled={csvImporting}
                      className="flex-1 btn-primary py-2"
                    >
                      Replace and import
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-sm text-gray-700">
                    Upload your GameChanger data here. Use a stats export from GameChanger (CSV). Batting and pitching stats that match our app will be imported for this game; fielding and other columns are not imported yet.
                  </p>
                  <input
                    ref={csvFileInputRef}
                    type="file"
                    accept=".csv"
                    className="hidden"
                    onChange={handleCsvFileSelected}
                  />
                  <button
                    type="button"
                    onClick={() => csvFileInputRef.current?.click()}
                    disabled={csvImporting}
                    className="btn-primary w-full flex items-center justify-center gap-2 py-2"
                  >
                    <Upload size={18} />
                    {csvImporting ? 'Importing…' : 'Choose CSV file'}
                  </button>
                </>
              )}
              {csvImportStatus && (
                <p className="text-sm text-gray-500">{csvImportStatus}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
